import { Injectable, signal } from "@angular/core";
import { Data } from "@angular/router";
import { BehaviorSubject, interval, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { VoucherService } from "src/app/services/vouchers.service";

export interface VoucherData {
  id?: string;
  voucher_code?: string;
  discount_percentage?: number;
  expiry_date?: any;
  isSelected?: boolean;
  isDisabled?: boolean;
  isUsed?: boolean;
  changeExpiryText?: boolean;
  [key: string]: any;
}

@Injectable({ providedIn: null })
export class VoucherManagementService {
  // State - BehaviorSubjects
  isChecked = signal<boolean>(false);
  typeOfVoucher = signal<string>('auto');
  selectedDiscountValueSignal = signal<number>(0);
  private readonly selectedDiscountSubject = new BehaviorSubject<number>(0);
  readonly selectedDiscount$ = this.selectedDiscountSubject.asObservable();
  private discountVouchersDataSubject = new BehaviorSubject<VoucherData[]>([]);
  private selectedDiscountValueSubject = new BehaviorSubject<number>(0);
  private selectedDiscountVoucherDataSubject = new BehaviorSubject<VoucherData | null>(null);
  private discountVouchersStatusSubject = new BehaviorSubject<boolean>(false);
  private addCardPaymentStatusSubject = new BehaviorSubject<boolean>(false);
  public purchaseDetail = {} as Data;

  // Expiry check interval
  private _expiryCheckInterval: Subscription | null = null;

  // Public observables
  discountVouchersData$ = this.discountVouchersDataSubject.asObservable();
  selectedDiscountValue$ = this.selectedDiscountValueSubject.asObservable();
  selectedDiscountVoucherData$ = this.selectedDiscountVoucherDataSubject.asObservable();
  discountVouchersStatus$ = this.discountVouchersStatusSubject.asObservable();
  addCardPaymentStatus$ = this.addCardPaymentStatusSubject.asObservable();

  // Getters for current values
  get discountVouchersData(): VoucherData[] {
    return this.discountVouchersDataSubject.value;
  }

  get selectedDiscountValue(): number {
    return this.selectedDiscountValueSubject.value;
  }

  get selectedDiscountVoucherData(): VoucherData | null {
    return this.selectedDiscountVoucherDataSubject.value;
  }

  get discountVouchersStatus(): boolean {
    return this.discountVouchersStatusSubject.value;
  }

  get addCardPaymentStatus(): boolean {
    return this.addCardPaymentStatusSubject.value;
  }

  get expiryCheckInterval(): Subscription | null {
    return this._expiryCheckInterval;
  }

  set expiryCheckInterval(value: Subscription | null) {
    this._expiryCheckInterval = value;
  }

  // Setters
  setDiscountVouchersStatus(status: boolean): void {
    this.discountVouchersStatusSubject.next(status);
  }

  setAddCardPaymentStatus(status: boolean): void {
    this.addCardPaymentStatusSubject.next(status);
  }

  // Expose VoucherService for external use
  constructor(private voucherService: VoucherService) { }

  fetchDiscountVouchersData(purchaseDetail$: any) {
    this.voucherService
      .getVoucherList()
      .pipe(take(1))
      .subscribe({
        next: (res: any[]) => {
          const currentTime = new Date().getTime();
          this.purchaseDetail = purchaseDetail$.value;
          const filteredVouchers = res.filter((voucher) => {
            const voucherExpiryDate = voucher?.expiry_date?.toDate()?.getTime();
            return (
              voucher?.discount_voucher === true &&
              voucher?.isRedeemed === false &&
              voucher?.hide === false &&
              voucherExpiryDate > currentTime &&
              (voucher?.supplier?.toLowerCase() === "general" ||
                voucher?.supplier?.toLowerCase() === purchaseDetail$.value?.real_name?.toLowerCase())
            );
          });
          const hasVouchers = filteredVouchers.length > 1;
          const mappedVouchers = filteredVouchers.map((voucher, index) => ({
            ...voucher,
            isSelected: hasVouchers && index === 0,
            isDisabled: false,
            changeExpiryText: false,
            auto_discount: hasVouchers && index === 0,
          }));
          this.discountVouchersDataSubject.next(mappedVouchers);
          if (mappedVouchers.length > 1) {
            mappedVouchers[0].isSelected = true
            const autoSelectedData = {
              event: null,
              voucher: mappedVouchers[0],
              typeOfVoucher: this.typeOfVoucher(),
            }
            this.onVoucherSelect(autoSelectedData)
          }
        },
        error: (err) => {
          this.discountVouchersDataSubject.next([]);
          return err;
        },
      });
    this.startDiscountVouchersExpiryCheck();
  }

  startDiscountVouchersExpiryCheck() {
    try {
      this.expiryCheckInterval?.unsubscribe();
      this.expiryCheckInterval = interval(1000).subscribe(() => {
        const currentTime = new Date().getTime();
        const currentVouchers = this.discountVouchersDataSubject.value;
        const filteredVouchers = currentVouchers.filter((voucher) => {
          const voucherExpiryDate = voucher.expiry_date.toDate().getTime();
          const status = voucherExpiryDate > currentTime;
          const remainingTime = voucherExpiryDate - currentTime;
          if (remainingTime > 0 && remainingTime <= 60000) {
            this.updateExpiryTextInVoucher(voucher);
          }
          if (!status) {
            this.resetDiscountAmountOnVoucherExpiry(voucher);
          }
          return status;
        });
        this.discountVouchersDataSubject.next(filteredVouchers);
      });
    } catch (error) {
      return error;
    }
  }

  onVoucherSelect(emiteVoucherData: any) {
    const selectedVoucher = emiteVoucherData?.voucher;
    const triggerType = emiteVoucherData?.voucherEventsMixPanel;
    this.isChecked.set(emiteVoucherData.event?.detail?.checked ?? emiteVoucherData.voucher?.isSelected);
    const isVoucherAutoApplied = selectedVoucher.auto_discount && triggerType === "auto";
    selectedVoucher.autoAppliedVoucher = isVoucherAutoApplied ? true : false;
    this.typeOfVoucher.set("manual");
    if (selectedVoucher.isSelected) {
      this.selectedDiscountVoucherDataSubject.next({ ...selectedVoucher });
      const currentVouchers = this.discountVouchersDataSubject.value;
      const updatedVouchers = currentVouchers.map((voucher) => {
        const isSelectedVoucher = voucher === selectedVoucher;
        return {
          ...voucher,
          isSelected: isSelectedVoucher,
          isDisabled: !isSelectedVoucher,
          isUsed: !isSelectedVoucher,
        };
      });
      this.discountVouchersDataSubject.next(updatedVouchers);

      this.voucherService
        .getVoucherLookupData(selectedVoucher.voucher_code)
        .pipe(take(1))
        .subscribe((data: any[]) => {
          if (data.length > 0) {
            const voucherData: any = data[0];
            const voucherExpiryDate = voucherData.end_date.toDate().getTime();
            const now = new Date().getTime();
            if (voucherData.status && voucherExpiryDate > now) {
              this.voucherService.updateDiscountVoucherHideStatus(selectedVoucher.id, true);
              const discountValue = selectedVoucher?.discount_percentage / 100;
              this.selectedDiscountValueSubject.next(discountValue);
              this.purchaseDetail['voucher_discount'] = discountValue && discountValue > 0 ? discountValue : 0;

            } else {
              this.voucherService.showInvalidVoucherToast();
              this.voucherService.updateDiscountVoucherHideStatus(selectedVoucher.id, true);
              const filteredVouchers = this.discountVouchersDataSubject.value.filter((voucher) => voucher.id !== selectedVoucher.id);
              const resetVouchers = filteredVouchers.map((voucher) => ({
                ...voucher,
                isSelected: false,
                isDisabled: false,
                isUsed: false,
              }));
              this.discountVouchersDataSubject.next(resetVouchers);
              this.selectedDiscountValueSubject.next(0);
            }
          }
        });
    } else {
      this.resetVouchersUI(this.purchaseDetail);
    }
  }

  public resetDiscountVoucherHideStatus() {
    if (!this.addCardPaymentStatusSubject.value) {
      this.voucherService.updateDiscountVoucherHideStatus(this.selectedDiscountVoucherDataSubject.value?.id, false);
    }
  }

  public resetDiscountAmountOnVoucherExpiry(voucher: VoucherData): void {
    try {
      const currentVoucherId = this.selectedDiscountVoucherDataSubject.value?.id || "";
      if (voucher?.id === currentVoucherId) {
        this.resetVouchersUI({});
        this.voucherService.updateDiscountVoucherHideStatus(currentVoucherId, true);
      }
    } catch (error) {
      return error;
    }
  }

  public resetVouchersUI(purchaseDetail: any) {
    try {
      this.voucherService.updateDiscountVoucherHideStatus(this.selectedDiscountVoucherDataSubject.value?.id, false);
      this.selectedDiscountValueSubject.next(0);
      this.selectedDiscountVoucherDataSubject.next(null);
      const currentVouchers = this.discountVouchersDataSubject.value;
      const resetVouchers = currentVouchers.map((voucher) => ({
        ...voucher,
        isSelected: false,
        isDisabled: false,
        isUsed: false,
      }));
      this.discountVouchersDataSubject.next(resetVouchers);
    } catch (error) {
      return error;
    }
  }

  public updateExpiryTextInVoucher(expiredVoucher: VoucherData): void {
    const currentVouchers = this.discountVouchersDataSubject.value;
    const voucherIndex = currentVouchers.findIndex((voucher) => voucher.id === expiredVoucher.id);
    if (voucherIndex !== -1) {
      const updatedVouchers = [...currentVouchers];
      updatedVouchers[voucherIndex] = {
        ...updatedVouchers[voucherIndex],
        changeExpiryText: true,
      };
      this.discountVouchersDataSubject.next(updatedVouchers);
    }
  }

  // Cleanup
  public cleanup(): void {
    this.expiryCheckInterval?.unsubscribe();
    this.expiryCheckInterval = null;
    this.discountVouchersDataSubject.next([]);
    this.selectedDiscountValueSubject.next(0);
    this.selectedDiscountVoucherDataSubject.next(null);
    this.discountVouchersStatusSubject.next(false);
    this.addCardPaymentStatusSubject.next(false);
  }
}
